import { useCallback, useEffect, useMemo, useState } from 'react';
import AmicaleApi from '../../../api/AmicaleApi';
import { clearAuthSession } from '../../../utils/authUtils';
import {
  setTokens,
  startProactiveTokenRefresh,
  stopProactiveTokenRefresh,
} from '../../../config/api.config';
import { AMICALE_STATUS, AmicaleContext } from './AmicaleContext';

const getCurrentBaseUrl = () => {
  if (typeof window === 'undefined') return '';
  return window.location.origin || '';
};

const persistAmicaleSession = ({ accessToken, refreshToken, user }) => {
  if (!accessToken || !refreshToken || !user) return false;
  try {
    setTokens(accessToken, refreshToken);
    localStorage.setItem('user', JSON.stringify(user));
    startProactiveTokenRefresh();
    return true;
  } catch (error) {
    console.error('[AmicaleProvider] failed to persist amicale session:', error);
    return false;
  }
};

const resetPreviousSession = () => {
  const cleared = clearAuthSession();
  if (cleared) {
    try {
      stopProactiveTokenRefresh();
    } catch {
      /* ignore */
    }
  }
};

const AmicaleProvider = ({ children }) => {
  const [status, setStatus] = useState(AMICALE_STATUS.LOADING);
  const [isAmical, setIsAmical] = useState(false);
  const [client, setClient] = useState(null);

  const detect = useCallback(async () => {
    setStatus(AMICALE_STATUS.LOADING);
    try {
      const baseUrl = getCurrentBaseUrl();
      const data = await AmicaleApi.checkUrl(baseUrl);
      const detectedAmical = Boolean(data?.isAmical);

      if (detectedAmical) {
        resetPreviousSession();
        const persisted = persistAmicaleSession({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          user: data.user,
        });

        if (!persisted) {
          throw new Error('amicale session payload missing');
        }
      }

      setIsAmical(detectedAmical);
      setClient(data?.client || null);
      setStatus(AMICALE_STATUS.READY);
    } catch (error) {
      console.error('[AmicaleProvider] detection failed:', error);
      setIsAmical(false);
      setClient(null);
      setStatus(AMICALE_STATUS.ERROR);
    }
  }, []);

  useEffect(() => {
    detect();
  }, [detect]);

  const value = useMemo(
    () => ({ status, isAmical, client, refresh: detect }),
    [status, isAmical, client, detect]
  );

  return (
    <AmicaleContext.Provider value={value}>{children}</AmicaleContext.Provider>
  );
};

export default AmicaleProvider;
